import { useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Modal } from '../ui/Modal';

interface MetricCardProps {
    title: string;
    mainFigure: string; // Formatted currency string
    trend?: {
        value: string;
        label: string;
        color: 'emerald' | 'amber' | 'red';
    };
    /** Rich breakdown shown in a modal when the card is tapped. */
    backContent: React.ReactNode;
    loading?: boolean;
    icon?: React.ElementType;
    frontAction?: React.ReactNode; // Action at the bottom left (replaces trend if provided)
    /** The one figure in its group that matters most (Safe-to-Spend): raised
     *  surface, gold top rule, larger figure. Use once per group. */
    featured?: boolean;
    topBadge?: React.ReactNode; // Small badge above the title (e.g. "★ Best month yet")
    subline?: React.ReactNode; // Supporting line below the main figure (e.g. "14 jobs · £601 avg")
    /** Modal title override (defaults to the card title). */
    detailTitle?: string;
}

/**
 * Financial metric card. Tapping anywhere on the card opens a modal with the
 * full breakdown (`backContent`).
 *
 * The tap target is a full-bleed button laid UNDER the content rather than a
 * button wrapping it: `frontAction` (Pay Yourself) is itself a button, and a
 * button inside a button is invalid HTML — browsers and screen readers handle
 * it inconsistently. The content is pointer-transparent, so a tap anywhere but
 * the action still lands on the card.
 */
export const MetricCard = ({
    title,
    mainFigure,
    trend,
    backContent,
    loading = false,
    icon: Icon,
    frontAction,
    featured = false,
    topBadge,
    subline,
    detailTitle,
}: MetricCardProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const trendColor: Record<string, string> = {
        emerald: 'text-success',
        amber: 'text-gold',
        red: 'text-danger',
    };

    // Long figures step down so £1,234,567 still fits a 300px rail tile.
    const figureSize = (() => {
        const len = mainFigure.length;
        if (featured) {
            if (len >= 10) return 'text-[1.75rem] md:text-[2rem]';
            if (len >= 8) return 'text-[2.125rem] md:text-[2.375rem]';
            return 'text-[2.5rem] md:text-[2.75rem]';
        }
        if (len >= 10) return 'text-[1.5rem]';
        if (len >= 8) return 'text-[1.75rem] md:text-[1.875rem]';
        return 'text-[2rem] md:text-[2.125rem]';
    })();

    const figure = loading ? '—' : mainFigure;

    return (
        <>
            <div
                className={`fd-card fd-card-interactive ${featured ? 'fd-card-raised' : ''}
                    group h-full min-h-[10.5rem] flex flex-col gap-4 p-[var(--fd-card-pad)] [--fd-card-pad:1.25rem] md:[--fd-card-pad:1.5rem]`}
            >
                <button
                    type="button"
                    onClick={() => setIsOpen(true)}
                    aria-label={`${title}: ${figure}. Show breakdown`}
                    className="absolute inset-0 z-0 w-full h-full rounded-[inherit] cursor-pointer focus-visible:outline-offset-[-2px]"
                />

                <div className="relative z-[1] pointer-events-none flex items-start justify-between gap-3">
                    <div className="flex flex-col gap-1.5 min-w-0">
                        {topBadge && <div className="mb-0.5">{topBadge}</div>}
                        <span className="fd-card-label">{title}</span>
                        <p className={`fd-kpi break-words ${figureSize}`} aria-hidden="true">
                            {figure}
                        </p>
                        {subline && !loading && (
                            <div className="text-fd-sm-tight text-fg-3 font-mono tabular-nums">
                                {subline}
                            </div>
                        )}
                    </div>
                    {Icon && (
                        <span
                            aria-hidden="true"
                            className={`shrink-0 grid place-items-center w-10 h-10 rounded-[var(--fd-r-md)] border ${
                                featured
                                    ? 'bg-gold-fill/10 border-gold/35 text-gold'
                                    : 'bg-fg-3/8 border-[var(--fd-line)] text-fg-3'
                            }`}
                        >
                            <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
                        </span>
                    )}
                </div>

                {/* Full-bleed footer band (fd-card-footer-strip reaches the card's
                    edges through --fd-card-pad, set above to match the padding). */}
                <div className="fd-card-footer-strip relative z-[1] pointer-events-none">
                    {frontAction ? (
                        <div className="pointer-events-auto">{frontAction}</div>
                    ) : trend ? (
                        <div className="flex items-baseline gap-2">
                            <span className={`text-base font-semibold font-mono ${trendColor[trend.color]}`}>
                                {trend.value}
                            </span>
                            <span className="text-fd-3xs text-fg-4 font-medium uppercase tracking-wider">
                                {trend.label}
                            </span>
                        </div>
                    ) : (
                        <span />
                    )}

                    <span
                        aria-hidden="true"
                        className="inline-flex items-center gap-1 text-fd-3xs font-semibold uppercase tracking-[0.14em] text-fg-4 group-hover:text-fg transition-colors"
                    >
                        Details <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                </div>
            </div>

            <Modal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                title={detailTitle || title}
                icon={Icon}
            >
                {backContent}
            </Modal>
        </>
    );
};
